import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import RequireModule from '@/core/modules/RequireModule';
import RequirePermission from '@/auth/RequirePermission';
import { Modules } from '@/core/modules/moduleKeys';
import { Perm } from '@/auth/permissions';

// Pages load when first opened (NFR-04).
const LearningPeoplePage = lazy(() => import('./pages/LearningPeoplePage'));
const LearningOrganisationPage = lazy(() => import('./pages/LearningOrganisationPage'));
const LearningGroupsPage = lazy(() => import('./pages/LearningGroupsPage'));
const LearningSettingsPage = lazy(() => import('./pages/LearningSettingsPage'));
const LearningLibraryPage = lazy(() => import('./pages/LearningLibraryPage'));
const LearningItemPage = lazy(() => import('./pages/LearningItemPage'));
const LearningLibrarySetupPage = lazy(() => import('./pages/LearningLibrarySetupPage'));
const LearningAssignmentsPage = lazy(() => import('./pages/LearningAssignmentsPage'));
const LearningApprovalsPage = lazy(() => import('./pages/LearningApprovalsPage'));
const LearningQuestionBanksPage = lazy(() => import('./pages/LearningQuestionBanksPage'));
const LearningGradingPage = lazy(() => import('./pages/LearningGradingPage'));
const LearningCertificatesPage = lazy(() => import('./pages/LearningCertificatesPage'));
const LearningCompliancePage = lazy(() => import('./pages/LearningCompliancePage'));
const LearningNoticesPage = lazy(() => import('./pages/LearningNoticesPage'));
const LearningSessionsPage = lazy(() => import('./pages/LearningSessionsPage'));
const LearningReportsPage = lazy(() => import('./pages/LearningReportsPage'));
const LearningDashboardPage = lazy(() => import('./pages/LearningDashboardPage'));
const LearningTeamPage = lazy(() => import('./pages/LearningTeamPage'));
const LearningIntegrationsPage = lazy(() => import('./pages/LearningIntegrationsPage'));
const LearningEngagementPage = lazy(() => import('./pages/LearningEngagementPage'));

const gate = (anyOf: string[], page: React.ReactNode) => (
  <RequireModule module={Modules.Learning}>
    <RequirePermission anyOf={anyOf}>{page}</RequirePermission>
  </RequireModule>
);

const LIBRARY = [Perm.Learning.Author, Perm.Learning.Publish, Perm.Learning.Assign, Perm.Learning.Configure, Perm.Learning.ViewRecords, Perm.Learning.Reports];

/** Learning administration (docs §261 on). */
export const learningRoutes: RouteObject[] = [
  { path: 'learning/people', element: gate([Perm.Learning.ViewRecords, Perm.Learning.ManageUsers], <LearningPeoplePage />) },
  { path: 'learning/organisation', element: gate([Perm.Learning.ManageUsers, Perm.Learning.ViewRecords], <LearningOrganisationPage />) },
  { path: 'learning/groups', element: gate([Perm.Learning.ManageUsers, Perm.Learning.Assign], <LearningGroupsPage />) },
  { path: 'learning/settings', element: gate([Perm.Learning.Configure, Perm.Learning.ManageUsers], <LearningSettingsPage />) },
  // The content library (docs §263).
  { path: 'learning/library', element: gate(LIBRARY, <LearningLibraryPage />) },
  { path: 'learning/library/:id', element: gate(LIBRARY, <LearningItemPage />) },
  { path: 'learning/library-setup', element: gate([Perm.Learning.Publish, Perm.Learning.Configure, Perm.Learning.Reports], <LearningLibrarySetupPage />) },
  // Assignment and the learning record (docs §267).
  { path: 'learning/assignments', element: gate([Perm.Learning.Assign], <LearningAssignmentsPage />) },
  { path: 'learning/approvals', element: gate([Perm.Learning.Approve], <LearningApprovalsPage />) },
  // Quizzes (docs §268).
  { path: 'learning/question-banks', element: gate([Perm.Learning.Author, Perm.Learning.Publish, Perm.Learning.ManageSessions, Perm.Learning.Reports], <LearningQuestionBanksPage />) },
  { path: 'learning/grading', element: gate([Perm.Learning.ManageSessions, Perm.Learning.Author, Perm.Learning.Publish], <LearningGradingPage />) },
  // Certificates and compliance (docs §269).
  { path: 'learning/certificates', element: gate([Perm.Learning.Publish, Perm.Learning.Configure, Perm.Learning.ViewRecords, Perm.Learning.Reports], <LearningCertificatesPage />) },
  { path: 'learning/compliance', element: gate([Perm.Learning.ViewRecords, Perm.Learning.Reports], <LearningCompliancePage />) },
  // Notices (docs §270).
  // Sessions (docs §271).
  { path: 'learning/sessions', element: gate([Perm.Learning.ManageSessions], <LearningSessionsPage />) },
  { path: 'learning/notices', element: gate([Perm.Learning.Configure, Perm.Learning.Assign, Perm.Learning.Reports], <LearningNoticesPage />) },
  // Reports and dashboards (docs §272).
  { path: 'learning/reports', element: gate([Perm.Learning.Reports, Perm.Learning.ViewRecords], <LearningReportsPage />) },
  { path: 'learning/dashboard', element: gate([Perm.Learning.Reports, Perm.Learning.ViewRecords], <LearningDashboardPage />) },
  { path: 'learning/team', element: gate([Perm.Learning.Reports, Perm.Learning.ViewRecords, Perm.Learning.Assign], <LearningTeamPage />) },
  // Integrations and administration (docs §273).
  { path: 'learning/integrations', element: gate([Perm.Learning.Configure], <LearningIntegrationsPage />) },
  // Engagement: moderation, surveys, skills (docs §274).
  { path: 'learning/engagement', element: gate([Perm.Learning.Publish, Perm.Learning.Configure, Perm.Learning.Reports, Perm.Learning.ManageUsers, Perm.Learning.ViewRecords, Perm.Learning.Assign],
    <LearningEngagementPage />) },
];
